import { RouteObject, ScrollRestoration } from 'react-router-dom';

import { mainLoader, authReady } from '@layouts/main.loader';
import HydrateFallback from './hydrate-fallback';
import App from './App';
import ErrorElement from './ErrorElement';
import ChooseTemplateRoutes from '@micros/choose-template/choose-template.routes';
import EditProjectRoutes from './micros/edit-project/edit-project.routes';
import ProjectRoot from './micros/shared-create-and-edit-components/project-root';
import NotFoundView from './micros/404/not-found.view';
import GlobalDocumentHead from './document-head/global-document-head';
import AdminHomepageRoutes from './micros/homepage/homepage.routes';
import blAuth from './connection/bl-auth/bl-auth';

import 'react-toastify/dist/ReactToastify.css';
import DebugWatermarkCanvas from './components/watermark/debug-watermark-canvas';

const routes: RouteObject[] = [
  {
    path: ':theme?/*',
    id: 'app-catch-all',
    element: (
      <>
        <ScrollRestoration
          getKey={(location) => {
            /*
              if we have search params, or are on the stats page,
              don't force the page to restore scroll position or we get unintended jank when
              because the page will jump to the top whenever we "filter"
            */
            if (location.search || location.pathname.includes('/stats/')) {
              return location.pathname;
            }

            // otherwise, always restore
            return location.key;
          }}
        />
        {/* Use GlobalDocumentHead here instead of inside main.tsx so we have access to react-router context inside it */}
        <GlobalDocumentHead />
        <App />
      </>
    ),
    shouldRevalidate: () => false,
    loader: mainLoader,
    HydrateFallback: HydrateFallback,
    children: [
      {
        path: '*',
        id: 'main',
        async lazy() {
          await authReady; // wait for mainLoader to complete auth setup before checking tokens

          if (!blAuth.idToken || !blAuth.refreshToken) {
            return { Component: null };
          }
          const MainView = await import('./layouts/main.view');
          return { Component: MainView.default };
        },
        shouldRevalidate: () => false, // prevents re-running loader when navigating between child routes
        errorElement: <ErrorElement />,
        children: [
          {
            index: true,
            id: 'index',
            loader: () => (window.location.href = '/admin/projects'),
          },
          {
            id: 'debug-canvas',
            path: 'canvas/*',
            element: <DebugWatermarkCanvas />,
          },
          {
            path: 'admin/*',
            id: 'admin-homepage',
            children: AdminHomepageRoutes,
            ErrorBoundary: () => <div>Admin Error Boundary</div>,
          },
          {
            path: 'choose/*',
            id: 'create-template',
            children: ChooseTemplateRoutes,
            ErrorBoundary: () => <div>Create Template Error Boundary</div>,
          },
          {
            path: 'project/*',
            id: 'project-root',
            shouldRevalidate: () => false,
            Component: ProjectRoot,
            ErrorBoundary: () => <div>Project Root Error Boundary</div>,
            children: [
              {
                path: 'edit/*',
                id: 'edit-project',
                children: EditProjectRoutes,
                shouldRevalidate: () => false,
                ErrorBoundary: () => <div>Edit Project Error Boundary</div>,
              },
            ],
          },
          {
            path: '*',
            element: <NotFoundView />,
          },
        ],
      },
    ],
  },
];

export default routes;
